import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, ShieldCheck, FileDown } from "lucide-react";

function downloadPdf(url) {
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const Section = ({ title, children }) => (
  <section className="mb-6 break-inside-avoid">
    <h3 className="mb-2 border-b border-slate-300 pb-1 font-display text-sm font-bold uppercase tracking-wide text-slate-700">{title}</h3>
    {children}
  </section>
);
const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-0.5 text-xs"><span className="text-slate-400">{k}</span><span className="font-medium text-slate-800">{v || "—"}</span></div>
);

export default function DefensePacketView() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  useEffect(() => { api.packet(id).then(setP).catch(() => {}); }, [id]);
  if (!p) return <div className="text-sm text-slate-500">Loading packet…</div>;
  const s = p.snapshot || {};
  const ev = s.evaluation || {};
  const crits = ev.criterion_results || [];
  const missing = crits.filter((r) => r.status !== "SATISFIED");

  return (
    <div>
      <div className="mb-3 flex items-center justify-between no-print">
        <Button variant="ghost" size="sm" className="text-slate-500" onClick={() => nav("/defense-packets")}><ArrowLeft className="mr-1 h-4 w-4" /> Evidence Packets</Button>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="border-teal-600 text-teal-700 hover:bg-teal-50" data-testid="export-packet-pdf-view" onClick={() => downloadPdf(api.exportPacketPdfUrl(p.order_id))}><FileDown className="mr-1.5 h-4 w-4" /> Export Evidence Packet (PDF)</Button>
          <Button size="sm" className="bg-slate-900 hover:bg-slate-800" data-testid="print-packet" onClick={() => window.print()}><Printer className="mr-1.5 h-4 w-4" /> Print</Button>
        </div>
      </div>

      <div className="print-full mx-auto max-w-3xl rounded-lg border border-slate-300 bg-white p-8 shadow-sm" data-testid="defense-packet-doc">
        <div className="mb-6 flex items-start justify-between border-b-2 border-slate-800 pb-4">
          <div>
            <div className="flex items-center gap-2 font-display text-xl font-bold text-slate-900"><ShieldCheck className="h-6 w-6 text-teal-600" /> RequiSure Evidence Packet</div>
            <div className="mt-1 text-xs text-slate-500">Concept Prototype 0.2 · Developed by Inforno HealthTech · Synthetic Demonstration Environment · Includes Reimbursement Defense View</div>
          </div>
          <div className="text-right text-xs">
            <div className="font-mono font-semibold text-slate-800">{p.packet_number}</div>
            <div className="text-slate-500">{new Date(p.created_at).toLocaleString()}</div>
            <div className="mt-1"><StatusBadge status={s.final_status} size="sm" /></div>
          </div>
        </div>

        <Section title="Readiness Decision">
          <div className="mb-2 flex flex-wrap items-center gap-3">
            <StatusBadge status={s.final_status} size="sm" />
            <span className="text-xs text-slate-500">Readiness color: <span className="font-semibold text-slate-700">{s.readiness_color}</span></span>
            {s.risk_flag && <span className="rounded bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700">RED — REQUIRES VALIDATION</span>}
            {s.overridden && <span className="rounded bg-slate-800 px-2 py-0.5 text-[10px] font-semibold text-white">OVERRIDDEN — original retained</span>}
          </div>
          <div className="text-xs text-slate-700">{ev.summary || "No evaluation summary available."}</div>
          <div className="mt-1 text-[11px] text-slate-500">This packet is a readiness-evidence record for the demonstration rule set. It is not evidence of coverage or payment.</div>
        </Section>

        <Section title="Order Snapshot">
          <div className="grid grid-cols-2 gap-x-8">
            <Row k="Order #" v={s.order?.order_number} /><Row k="Patient (synthetic)" v={s.order?.patient_name} />
            <Row k="Synthetic patient ID" v={s.order?.synthetic_patient_id} /><Row k="DOB (demo)" v={s.order?.dob_demo} />
            <Row k="Ordering provider" v={s.order?.provider_name} /><Row k="Test ordered" v={s.order?.test_name} />
            <Row k="Payer context" v={s.order?.payer_name} /><Row k="Plan" v={s.order?.plan} />
            <Row k="Jurisdiction" v={s.order?.jurisdiction} /><Row k="Date of service" v={s.order?.service_date} />
            <Row k="Diagnosis (demo)" v={s.order?.diagnosis_demo} /><Row k="CPT/HCPCS (demo)" v={s.order?.cpt_demo} />
          </div>
        </Section>

        <Section title="Requirements Evaluated">
          {crits.length === 0 ? <div className="text-xs text-slate-500">No criteria evaluated (unsupported scenario).</div> :
            crits.map((r) => (
              <div key={r.criterion_code} className="mb-1.5 flex items-start justify-between gap-3 border-b border-slate-100 pb-1.5 text-xs">
                <div><span className="font-semibold text-slate-800">Criterion {r.criterion_code}:</span> {r.description}<div className="text-slate-500">{r.reason}</div></div>
                <StatusBadge status={r.status} size="sm" />
              </div>
            ))}
        </Section>

        <Section title="Verified Evidence & Source Excerpts">
          {(ev.facts || []).length === 0 ? <div className="text-xs text-slate-500">No verifiable evidence extracted from the submitted documentation.</div> :
            (ev.facts || []).map((f, i) => (
              <div key={i} className="mb-1.5 rounded border border-slate-200 p-2 text-xs">
                <div className="font-semibold text-slate-700">{f.fact_type.replace(/_/g, " ")} · conf {Math.round(f.confidence * 100)}%{f.ambiguous ? " · ambiguous" : ""}</div>
                <div className="italic text-slate-600">“{f.source_quote}” <span className="text-slate-400">[{f.character_start}-{f.character_end}] verified</span></div>
              </div>
            ))}
        </Section>

        <Section title="Missing / Unresolved Evidence">
          {missing.length === 0 ? <div className="text-xs text-emerald-700">All evaluated criteria were satisfied within the demonstration rule set.</div> :
            missing.map((r) => (
              <div key={r.criterion_code} className="mb-1.5 text-xs">
                <span className="font-semibold text-amber-800">Criterion {r.criterion_code}:</span> <span className="text-slate-700">{r.reason}</span>
                {r.next_action && <div className="text-slate-500">→ {r.next_action}</div>}
              </div>
            ))}
        </Section>

        <Section title="Order vs. Clinical Record Reconciliation">
          {(s.reconciliation || []).length === 0 ? <div className="text-xs text-slate-500">No reconciliation available for this scenario.</div> : (
            <table className="w-full text-xs" data-testid="packet-reconciliation">
              <thead className="text-left text-[10px] uppercase tracking-wide text-slate-400">
                <tr><th className="py-1 pr-3 font-semibold">Item</th><th className="py-1 pr-3 font-semibold">On order</th><th className="py-1 pr-3 font-semibold">In record</th><th className="py-1 font-semibold">Status</th></tr>
              </thead>
              <tbody>
                {s.reconciliation.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100 align-top"><td className="py-1 pr-3 font-medium text-slate-700">{r.item}</td><td className="py-1 pr-3 text-slate-600">{r.order_value || "—"}</td><td className="py-1 pr-3 text-slate-600">{r.record_value}</td><td className="py-1 font-semibold text-slate-700">{r.status}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        <Section title="Testing History">
          {(() => {
            const th = s.testing_history;
            if (!th || th.integrated === false) return <div className="text-xs text-slate-500" data-testid="packet-testing-history">Prior-testing history source not integrated in this demonstration (future integration). Cannot confirm whether prior testing exists.</div>;
            if (th.available === false || !th.count) return <div className="text-xs text-slate-600" data-testid="packet-testing-history">No prior testing found in the available records (reflects accessible records only; not confirmation that none exists). Frequency evaluation: {th.frequency_eval || "Unable to determine"}.</div>;
            return (<div className="text-xs text-slate-700" data-testid="packet-testing-history"><div className="mb-1">Prior tests in available record: <strong>{th.count}</strong> · Last relevant: {th.last_date || "—"}</div>{(th.tests || []).map((t, i) => <div key={i}>• {t.panel} — {t.date}</div>)}<div className="mt-1 text-[11px] text-slate-500">Frequency evaluation: {th.frequency_eval || "Awaiting Authoritative Rule"}. Source: {th.source || "—"}.</div></div>);
          })()}
        </Section>

        <Section title="Source Coverage">
          {s.source_coverage ? (
            <div className="grid grid-cols-2 gap-x-8 text-xs" data-testid="packet-source-coverage">
              <div><div className="mb-1 text-[10px] font-semibold uppercase text-emerald-700">Evaluated / available</div>{(s.source_coverage.evaluated || []).map((x, i) => <div key={i} className="text-slate-700">• {x}</div>)}</div>
              <div><div className="mb-1 text-[10px] font-semibold uppercase text-slate-500">Not available / not integrated</div>{(s.source_coverage.not_available || []).map((x, i) => <div key={i} className="text-slate-500">• {x}</div>)}</div>
            </div>
          ) : <div className="text-xs text-slate-500">Source coverage not recorded.</div>}
        </Section>

        <Section title="Rule / Policy Provenance">
          {s.policy_id ? (
            <>
              <Row k="Policy" v={s.policy_id} />
              <Row k="Version" v={s.policy_version} /><Row k="Effective date" v={s.policy_effective_date} />
              <Row k="Source" v="Synthetic demonstration content — not CMS" />
              <Row k="MAC context" v="Novitas — Jurisdiction H / Texas (demonstration)" />
              <div className="mt-1 text-[11px] text-slate-500">Specific controlling Novitas LCD/Article: To Be Confirmed. Does not represent actual CMS coverage policy.</div>
            </>
          ) : (
            <>
              <Row k="Policy" v="None applicable" />
              <div className="mt-1 text-[11px] text-slate-500">No approved applicable policy is configured for this payer / test scope. RequiSure does not borrow another payer's rule and cannot responsibly determine readiness.</div>
            </>
          )}
        </Section>

        <Section title="Authorization & Release">
          <Row k="Prior authorization" v={`${s.prior_auth?.status || "Not Evaluated"} · owner: ${s.prior_auth?.owner || "To Be Confirmed"}`} />
          <Row k="ABN" v={`${s.abn?.status || "Not Evaluated"} · owner: ${s.abn?.owner || "To Be Confirmed"}`} />
          <Row k="Order release" v={s.released_simulated ? "Released (SIMULATED — no external LIS)" : "Not released"} />
        </Section>

        <Section title="Claim Outcome (Synthetic / Future RCM)">
          {s.outcome ? (
            <>
              <Row k="Status" v={s.outcome.status} />
              <Row k="Amount (synthetic)" v={s.outcome.amount} />
              <Row k="Denial reason" v={s.outcome.denial_reason || "—"} />
              <Row k="Outcome date" v={s.outcome.outcome_date} />
            </>
          ) : <div className="text-xs text-slate-500">No synthetic outcome recorded. Claim submission/adjudication is a future RCM integration.</div>}
        </Section>

        <Section title="Clarification History">
          {(s.clarifications || []).length === 0 ? <div className="text-xs text-slate-500">None.</div> :
            s.clarifications.map((c) => <div key={c.id} className="mb-1 text-xs text-slate-700">• [{c.state}] {c.message}</div>)}
        </Section>

        <Section title="Human Review & Override History">
          {(s.reviews || []).length === 0 && (s.overrides || []).length === 0 ? <div className="text-xs text-slate-500">None.</div> : <>
            {(s.reviews || []).map((r) => <div key={r.id} className="mb-1 text-xs text-slate-700">• Review [{r.state}]: {r.reason} {r.resolution ? `— ${r.resolution}` : ""}</div>)}
            {(s.overrides || []).map((o) => <div key={o.id} className="mb-1 text-xs text-slate-700">• Override {o.original_status} → {o.new_status}: {o.justification} ({o.reviewer}) — original automated decision retained</div>)}
          </>}
        </Section>

        <Section title="Document / Version History">
          {(s.documents || s.order?.documents || []).length === 0 ? (
            <div className="text-xs text-slate-500">Clinical document versions are preserved immutably and are replayable in the order's Decision Replay history.</div>
          ) : (s.documents || s.order?.documents || []).map((d) => (
            <div key={d.id || d.version} className="mb-1 flex justify-between gap-3 text-xs">
              <span className="text-slate-700">Version {d.version} — {d.label}</span>
              <span className="font-mono text-slate-400">{d.created_at ? new Date(d.created_at).toLocaleString() : ""}</span>
            </div>
          ))}
        </Section>

        <Section title="Audit Timeline">
          {(s.audit || []).map((a) => (
            <div key={a.id} className="flex justify-between gap-3 py-0.5 text-[11px]">
              <span className="text-slate-700">{a.event_type.replace(/_/g, " ")} — {a.actor}</span>
              <span className="font-mono text-slate-400">{new Date(a.timestamp).toLocaleString()} · {a.id.slice(0, 8)}</span>
            </div>
          ))}
        </Section>

        <div className="mt-6 rounded border border-slate-300 bg-slate-50 p-3 text-center text-[11px] font-semibold text-slate-500">
          Concept Prototype / Synthetic Data / Not Evidence of Coverage
        </div>
      </div>
    </div>
  );
}
